/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { I18n } from "i18n-js";
import enUSJsonTranslations from "i18n-js/json/en.json";
import enUSYamlTranslations from "../../../../config/locales/en.yml";
import gatewayFetch from "utilities/gatewayFetch";

// This map falls back to available regions the navigator language is not supported
const baseLocaleMap: { [key: string]: string } = {
  en: "en",
  it: "it",
  pt: "pt-BR",
  fr: "fr-FR",
  de: "de-DE",
  es: "es-ES",
  hi: "hi-IN",
  id: "id-ID",
  ph: "ph-PH",
  tr: "tr-TR",
  bn: "bn-IN",
  "en-US": "en",
  "pt-BR": "pt-BR",
  "fr-FR": "fr-FR",
  "de-DE": "de-DE",
  "it-IT": "it",
  "es-ES": "es-ES",
  "hi-IN": "hi-IN",
  "id-ID": "id-ID",
  "ph-PH": "ph-PH",
  "tr-TR": "tr-TR",
  "bn-IN": "bn-IN",
};

// QA languages to add for non-production environments only
const qaLocales: { [key: string]: string } = {
  "en-ZA": "en-ZA", // testing only, never to be in production
  // Below to be moved to the baseLocaleMap once they are ready for production
};

const localeJsonPathMap: { [key: string]: string } = {
  en: "en",
  it: "it",
  "pt-BR": "pt-BR",
  "fr-FR": "fr-FR",
  "de-DE": "de-DE",
  "es-ES": "es-ES",
  "en-ZA": "en-ZA",
  "hi-IN": "hi-IN",
  "id-ID": "id",
  "tr-TR": "tr",
  "bn-IN": "bn",
  // There are no library supported json files for this language so we are handling it ourselves
  "ph-PH": "ph-PH",
};

const customJsonEntry = ["ph-PH"];

const LANGUAGE_PICKER_ENABLED = process.env.LANGUAGE_PICKER_ENABLED === "true";

// QA_LANGUAGE_ENABLED is used to enable the QA language in the language picker STAGE ONLY
const localeMap = {
  ...baseLocaleMap,
  ...(process.env.QA_LANGUAGE_ENABLED === "true" ? qaLocales : {}),
};

globalThis.I18n = new I18n(enUSJsonTranslations, {
  locale: "en",
});
globalThis.I18n.store(enUSYamlTranslations);

// Error if we having a missing translation and fallback to English
globalThis.I18n.missingTranslation.register(
  "phonos",
  (i18n, scope, options) => {
    newrelic.noticeError(
      new MissingTranslationError(scope.toString(), i18n.locale)
    );

    //Load and check if there's an English translation
    if (i18n.locale !== "en" && !options?.skipEnglishFallback) {
      const oldLocale = i18n.locale;
      i18n.locale = "en";
      const englishTranslation = i18n.translate(scope, {
        skipEnglishFallback: true,
      });
      i18n.locale = oldLocale;
      return englishTranslation;
    }

    return scope.toString();
  }
);
globalThis.I18n.missingBehavior = "phonos";

const originalInterpolate = globalThis.I18n.interpolate;
globalThis.I18n.interpolate = function (
  i18n: I18n,
  message: string,
  options: any
): string {
  const result = originalInterpolate(i18n, message, options);
  if (result.match(/\[missing ".*?" value\]/)) {
    newrelic.noticeError(
      new MissingInterpolationError(message, i18n.locale, options)
    );
  }
  return result;
};

class MissingTranslationError extends Error {
  constructor(scope: string, locale: string) {
    super(`Missing ${locale} translation: ${scope}`);
    this.name = "MissingTranslationError";
  }
}

class MissingInterpolationError extends Error {
  constructor(scope: string, locale: string, options: any) {
    super(
      `Missing ${locale} interpolation: ${scope}, given: ${JSON.stringify(
        options
      )}`
    );
    this.name = "MissingInterpolationError";
  }
}

export const getSimpleLocale = (): string => {
  return globalThis.I18n.locale.split("-")[0];
};

export const getFontForLocale = (): string => {
  const locale = getSimpleLocale();
  console.log("locale", locale);
  switch (locale) {
    case "bn":
      return "NotoSansBengali";
    case "hi":
      return "NotoSansDevanagari";
    default:
      return "Arial";
  }
};

export const intlPercentFormatter = (): Intl.NumberFormat =>
  new Intl.NumberFormat(globalThis.I18n.locale, {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

export const saveUserSettings = async ({
  locale,
}: {
  locale: string;
}): Promise<void> => {
  // Storing this on the backend, so we can access the locale in the API when needed.
  const timezone = new Date().getTimezoneOffset();
  const data = { preferred_language: locale, timezone };

  const response = await gatewayFetch(
    `/api/v1/user`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    },
    { reportErrors: false }
  );

  if (response.ok) {
    localStorage.setItem("preferred_language", locale);
  } else {
    throw new Error(`setUserLanguage failed. Status: ${response.status}.`);
  }
};

export const getUserLocale = (): string | null => {
  return localStorage.getItem("preferred_language");
};

export const changeLocale = async (newLocale: string): Promise<void> => {
  await saveUserSettings({ locale: newLocale });
  window.location.reload();
};

class LocaleSetterError extends Error {
  constructor(error: Error) {
    super("Failed to load locale");
    this.name = "LocaleSetterError";
  }
}

export const initializeLocale = async (): Promise<void> => {
  let userLocale: string | null = LANGUAGE_PICKER_ENABLED
    ? getUserLocale()
    : "en";
  try {
    if (!userLocale) {
      // If no user locale is found, fall back to the default locale and update the user's preferred language
      const navigatorLocale = navigator.language;
      userLocale = localeMap[navigatorLocale] || "en";
      localStorage.setItem("preferred_language", userLocale);
    }

    const jsonPromise = customJsonEntry.includes(userLocale)
      ? import(
          `../../../../config/utility/locales/${localeJsonPathMap[userLocale]}.json`
        )
      : import(`i18n-js/json/${localeJsonPathMap[userLocale]}.json`);

    const [jsonTranslations, yamlTranslations] = await Promise.all([
      jsonPromise,
      import(`../../../../config/locales/${userLocale}.yml`),
    ]);

    const jsonTranslationsWithCorrectedKey = {
      [userLocale]: jsonTranslations.default[localeJsonPathMap[userLocale]],
    };

    globalThis.I18n.store(jsonTranslationsWithCorrectedKey);
    globalThis.I18n.store(yamlTranslations.default);
    globalThis.I18n.locale = userLocale;
  } catch (error) {
    console.warn(
      `Failed to load translations for locale ${userLocale}:`,
      error
    );
    newrelic.noticeError(new LocaleSetterError(error), {
      actualErrorName: error.name,
      actualErrorMessage: error.message,
    });
    // Fallback to 'en' locale
    localStorage.setItem("preferred_language", "en");
    globalThis.I18n.locale = "en";

    //TODO: Add a toast message to notify the user that the translations failed to load
  }
};
